import React, { useState } from 'react';
import { CheckCircle2, Circle, ArrowRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Engine-readiness onboarding checklist.
// Steps derive live from tenant data (GET /api/firm/onboarding) — the
// card simply renders what the backend computed, honestly.

export default function OnboardingChecklist({ state: stateProp, compact = false, onDismissed }) {
  const navigate = useNavigate();
  const [fetched, setFetched] = useState(null);
  const [open, setOpen] = useState(!compact);

  React.useEffect(() => {
    if (stateProp) return;
    let alive = true;
    api.get('/api/firm/onboarding')
      .then((r) => { if (alive) setFetched(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [stateProp]);

  const state = stateProp || fetched;
  // Stays visible until ALL onboarding steps are done — it can be
  // collapsed, but never dismissed, while steps remain pending.
  if (!state || state.ready) return null;

  const go = (action) => {
    if (!action?.path) return;
    let url = action.tab ? `${action.path}?tab=${action.tab}` : action.path;
    if (action.anchor) url += `#${action.anchor}`;
    navigate(url);
  };

  const doneCount = state.done;
  const total = state.total;
  const pct = state.completion_pct;
  const pending_titles = (state.steps || [])
    .filter((st) => st.status !== 'done')
    .map((st) => st.title);

  return (
    <div className={`card border-amber-200/80 bg-gradient-to-br from-amber-50/60 to-white h-full ${compact ? "!p-4" : ""}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 text-left w-full group"
            title={open ? 'Collapse checklist' : 'Expand checklist'}
          >
            <h3 className="text-sm font-bold text-navy-900">Get Caply working at full potential</h3>
            <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 shrink-0">
              {doneCount}/{total} ready
            </span>
            {open ? (
              <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-slate-600 shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 shrink-0" />
            )}
          </button>
          <p className="text-xs text-slate-500 mt-0.5">
            A few inputs unlock the smartest screening, scoring and recommendations for your firm.
          </p>
        </div>
      </div>

      {/* Pending hint while collapsed */}
      {!open && pending_titles.length > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">
          Next up: {pending_titles.slice(0, 2).join(', ')}
          {pending_titles.length > 2 ? ` +${pending_titles.length - 2} more` : ''}
        </p>
      )}

      {/* Progress bar */}
      <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-gold-500 to-gold-400 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Steps (collapsible) */}
      <div className={`mt-4 space-y-2 ${open ? "" : "hidden"}`}>
        {state.steps.map((step) => (
          <div
            key={step.id}
            className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition ${
              step.status === 'done'
                ? 'border-emerald-100 bg-emerald-50/50'
                : 'border-slate-200 bg-white hover:border-brand-300'
            }`}
          >
            {step.status === 'done' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
            ) : (
              <Circle className="w-4 h-4 text-gold-500 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-semibold ${step.status === 'done' ? 'text-slate-500 line-through' : 'text-navy-900'}`}>
                  {step.title}
                </span>
                {step.required && step.status === 'pending' && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gold-700 bg-gold-100 rounded px-1.5 py-0.5">
                    Required
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">{step.description}</p>
              {step.missing && step.missing.length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Still needed: {step.missing.join(', ')}.
                </p>
              )}
              {step.detail && <p className="text-[11px] text-slate-400 mt-0.5">Today: {step.detail}</p>}
              {compact && step.status === 'pending' && (
                <button
                  onClick={() => go(step.action)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 px-2.5 py-1.5 rounded-lg transition"
                >
                  {step.action?.label || 'Open'} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
            {!compact && step.status === 'pending' && (
              <button
                onClick={() => go(step.action)}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 px-2.5 py-1.5 rounded-lg transition"
              >
                {step.action?.label || 'Open'} <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {open && !compact && (
        <p className="mt-3 text-[11px] text-slate-400 flex items-start gap-1">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Steps re-check themselves automatically as you fill in information — no need to mark anything done.
        </p>
      )}
    </div>
  );
}
